<template>
    <div class="admin a-shell" :class="{ 'a-shell-nav-open': nav_open }">
        <aside class="a-sidebar">
            <div class="a-brand">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17 10 8h5l3 9M10 8 8 4H5"/></svg>
                <span>VueltaCiclista</span>
            </div>

            <nav class="a-nav">
                <router-link :to="{ name: 'Panel' }" class="a-nav-item" :class="{ 'is-active': $route.name == 'Panel' }" @click="nav_open = false">
                    <i class="bi bi-calendar-event"></i>{{ season_label_plural }}
                </router-link>
                <div class="a-nav-events">
                    <template v-for="ev in seasons" :key="ev.id">
                        <router-link :to="{ name: 'Stages', params: { season_id: ev.id } }" class="a-nav-event" :class="{ 'is-current': ev.id == season_id }" @click="nav_open = false">
                            <span class="a-nav-event-name">{{ ev.name }}</span>
                            <span v-if="live_season_id == ev.id" class="a-nav-live" title="En directo"></span>
                        </router-link>
                        <div v-if="ev.id == season_id" class="a-nav-sub">
                            <router-link v-for="tab in event_tabs" :key="tab.name" :to="{ name: tab.name, params: { season_id: ev.id } }"
                                class="a-nav-subitem" :class="{ 'is-active': is_tab_active(tab) }" @click="nav_open = false">{{ tab.label }}</router-link>
                        </div>
                    </template>
                </div>

                <div class="a-nav-sep"></div>
                <router-link :to="{ name: 'Users' }" class="a-nav-item" @click="nav_open = false"><i class="bi bi-people"></i>Usuarios</router-link>
                <router-link v-if="is_admin" :to="{ name: 'Settings' }" class="a-nav-item" @click="nav_open = false"><i class="bi bi-sliders"></i>Ajustes generales</router-link>
            </nav>

            <div class="a-account" v-if="$store.state.game.user">
                <div class="a-avatar">{{ ($store.state.game.user.username || '?').charAt(0).toUpperCase() }}</div>
                <div class="a-account-info">
                    <div class="a-account-name">{{ $store.state.game.user.username }}</div>
                    <div class="a-account-role">{{ role_label }}</div>
                </div>
                <button type="button" class="a-account-out" title="Cerrar sesión" @click="logout"><i class="bi bi-box-arrow-right"></i></button>
            </div>
        </aside>
        <div class="a-scrim" @click="nav_open = false"></div>

        <div class="a-main">
            <header class="a-topbar">
                <button type="button" class="btn btn-ghost btn-icon a-nav-toggle" title="Menú" @click="nav_open = !nav_open"><i class="bi bi-list"></i></button>
                <nav class="a-crumbs" aria-label="Ruta">
                    <template v-for="(crumb, i) in crumbs" :key="i">
                        <i v-if="i > 0" class="bi bi-chevron-right a-crumb-sep"></i>
                        <router-link v-if="crumb.to && i < crumbs.length - 1" :to="crumb.to" class="a-crumb">{{ crumb.label }}</router-link>
                        <span v-else class="a-crumb a-crumb-current">{{ crumb.label }}</span>
                    </template>
                </nav>
                <div class="a-topbar-right">
                    <span class="a-conn" :class="connected ? 'is-on' : 'is-off'" :title="connected ? 'Conectado al servidor' : 'Sin conexión con el servidor'">
                        <i class="bi" :class="connected ? 'bi-wifi' : 'bi-wifi-off'"></i>
                    </span>
                    <full-screen btn_class="btn btn-ghost btn-icon"></full-screen>
                </div>
            </header>

            <main class="a-content">
                <div class="a-content-inner" :class="{ 'a-content-wide': wide }">
                    <slot></slot>
                </div>
            </main>

            <LiveDock />
        </div>
    </div>
</template>

<script>
import utils from '@/../../shared/utils'
import FullScreen from '@/components/FullScreen.vue'
import LiveDock from '@/components/admin/LiveDock.vue'

// Frame shared by every admin screen: navigation (sidebar), where-am-I
// (breadcrumbs) and what's-live (LiveDock, always visible while something
// runs). Screens only provide their content.
export default {
    name: 'AdminLayout',
    components: { FullScreen, LiveDock },
    props: {
        // [{ label, to? }] - the last one is the current page.
        crumbs: { type: Array, default: () => [] },
        // Event this screen belongs to: expands it in the sidebar and makes
        // it the event this admin controls live (SELECT_EVENT).
        season_id: { type: [Number, String], default: null },
        // Wider content column (rundown, tables).
        wide: { type: Boolean, default: false },
    },
    data(){
        return {
            utils,
            nav_open: false,
            event_tabs: [
                { name: 'Stages', label: 'Etapas', also: ['StageRundown'] },
                { name: 'RankingsAdmin', label: 'Rankings' },
                { name: 'EventParticipants', label: 'Participantes' },
                { name: 'EventSettings', label: 'Configuración' },
            ],
        };
    },
    computed: {
        seasons(){
            return this.$store.state.game.seasons || [];
        },
        season_label_plural(){
            return utils.get_season_label(window.MODE, true);
        },
        is_admin(){
            const user = this.$store.state.game.user;
            return !!user && user.role == utils.USER_ROLES.ADMINISTRATOR;
        },
        role_label(){
            const user = this.$store.state.game.user;
            if(!user) return '';
            return user.role == utils.USER_ROLES.ASSISTANT ? 'Asistente' : 'Administrador';
        },
        connected(){
            return this.$store.state.network == 'connected';
        },
        // The event room this admin is attached to, when something is live
        // in it - gets the red dot in the sidebar.
        live_season_id(){
            const game = this.$store.state.game;
            const live = game.stage_playback || (game.state && ![utils.GAME_STATES.IDLE, utils.GAME_STATES.AUTH].includes(game.state));
            return live ? game.active_season_id : null;
        }
    },
    watch: {
        season_id(){
            this.select_event();
        }
    },
    mounted(){
        this.select_event();
        if(this.connected)
            window.network.wso.send(JSON.stringify({action: window.RPC.UPDATE_VIEW, params: {view: this.$route.name, activity: null}}));
        $('.modal-backdrop').remove();
    },
    methods: {
        // Opening an event makes it the one this admin drives live, so the
        // old "select an event" dropdown isn't needed.
        select_event(){
            if(window.MODE !== utils.APP_MODES.EVENT || !utils.is_valid_parameter(this.season_id))
                return;
            if(this.$store.state.game.active_season_id != this.season_id)
                window.network.send(window.RPC.SELECT_EVENT, Number(this.season_id));
        },
        is_tab_active(tab){
            return this.$route.name == tab.name || (tab.also || []).includes(this.$route.name);
        },
        logout(){
            utils.reset_client();
        }
    }
}
</script>

<style lang="scss">
@import '@/assets/scss/admin.scss';
</style>

<style lang="scss" scoped>
/* absolute, not fixed: fixed would make the shell its own stacking
   context, and Bootstrap's modal backdrop (appended to <body>) would then
   cover the modals rendered inside it. */
.a-shell{
    position: absolute;
    inset: 0;
    display: flex;
    background: var(--a-canvas);
    overflow: hidden;
}

/* --- Sidebar --- */
.a-sidebar{
    width: 248px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: var(--a-ink);
    color: rgba(255, 255, 255, 0.78);
    z-index: 30;
}
.a-brand{
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 18px 20px 14px;
    font-family: "Archivo", sans-serif;
    font-weight: 800;
    font-size: 1rem;
    color: #fff;
    svg{ color: var(--a-accent); }
}
.a-nav{
    flex: 1;
    overflow-y: auto;
    padding: 6px 10px 12px;
}
.a-nav-item{
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.82);
    text-decoration: none;
    font-weight: 600;
    i{ font-size: 1rem; opacity: 0.8; }
    &:hover{ background: rgba(255, 255, 255, 0.06); color: #fff; }
    &.is-active, &.router-link-exact-active{ background: rgba(255, 255, 255, 0.1); color: #fff; }
}
.a-nav-events{
    margin: 2px 0 4px 18px;
    padding-left: 10px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
}
.a-nav-event{
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.66);
    text-decoration: none;
    font-size: 0.85rem;
    &:hover{ background: rgba(255, 255, 255, 0.06); color: #fff; }
    &.is-current{ color: #fff; font-weight: 600; }
}
.a-nav-event-name{
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.a-nav-live{
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--a-live);
    box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.25);
    flex-shrink: 0;
}
.a-nav-sub{
    display: flex;
    flex-direction: column;
    margin: 2px 0 6px 8px;
}
.a-nav-subitem{
    padding: 5px 10px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.6);
    text-decoration: none;
    font-size: 0.82rem;
    &:hover{ color: #fff; background: rgba(255, 255, 255, 0.05); }
    &.is-active{ color: #fff; background: rgba(0, 167, 175, 0.22); }
}
.a-nav-sep{
    height: 1px;
    margin: 12px 8px;
    background: rgba(255, 255, 255, 0.1);
}
.a-account{
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.a-avatar{
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: var(--a-accent);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    flex-shrink: 0;
}
.a-account-info{ flex: 1; min-width: 0; line-height: 1.2; }
.a-account-name{ color: #fff; font-weight: 600; overflow: hidden; text-overflow: ellipsis; }
.a-account-role{ font-size: 0.75rem; color: rgba(255, 255, 255, 0.5); }
.a-account-out{
    background: none;
    border: 0;
    color: rgba(255, 255, 255, 0.6);
    padding: 6px;
    border-radius: 6px;
    &:hover{ color: #fff; background: rgba(255, 255, 255, 0.08); }
}

/* --- Main column --- */
.a-main{
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}
.a-topbar{
    height: 52px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px 0 24px;
    background: var(--a-surface);
    border-bottom: 1px solid var(--a-line);
}
.a-nav-toggle{ display: none; }
.a-crumbs{
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
    font-size: 0.875rem;
}
.a-crumb{
    color: var(--a-text-2);
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &:hover{ color: var(--a-text); text-decoration: underline; }
}
.a-crumb-current{ color: var(--a-text); font-weight: 600; &:hover{ text-decoration: none; } }
.a-crumb-sep{ font-size: 0.7rem; color: var(--a-text-3); }
.a-topbar-right{ display: flex; align-items: center; gap: 4px; }
.a-conn{
    width: 32px;
    text-align: center;
    &.is-on{ color: var(--a-ok); }
    &.is-off{ color: var(--a-live); }
}
.a-content{
    flex: 1;
    overflow-y: auto;
}
.a-content-inner{
    max-width: 1080px;
    margin: 0 auto;
    padding: 28px 32px 48px;
}
.a-content-wide{ max-width: 1320px; }

.a-scrim{ display: none; }

/* --- Narrow screens: sidebar becomes a drawer --- */
@media (max-width: 991.98px){
    .a-sidebar{
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        transform: translateX(-100%);
        transition: transform 0.2s ease;
    }
    .a-shell-nav-open .a-sidebar{ transform: none; }
    .a-shell-nav-open .a-scrim{
        display: block;
        position: fixed;
        inset: 0;
        background: rgba(23, 32, 43, 0.4);
        z-index: 20;
    }
    .a-nav-toggle{ display: inline-flex; }
    .a-topbar{ padding-left: 8px; }
    .a-content-inner{ padding: 20px 16px 40px; }
}
</style>
